import { REFRESH_TOKEN as REFRESH_TOKEN_KEY, TOKEN } from './constants';

// Shared refresh token mechanism - singleton lock prevents multiple simultaneous refresh calls
// Used by both Apollo Client (GraphQL) and Axios (REST API)
let isRefreshing = false;
let pendingRefreshPromise = null;
// Circuit breaker: once the server definitively rejects the refresh token (e.g. it is
// expired), further refresh attempts are short-circuited so callers stop hammering the
// refresh endpoint. Cleared on a successful refresh or a fresh login.
let refreshFailed = false;
// Invoked exactly once when refresh permanently fails, so callers don't each redirect.
let onRefreshFailure = null;

// Registers the handler that performs a single logout/redirect on permanent refresh failure.
export const setOnRefreshFailure = (handler) => {
  onRefreshFailure = handler;
};

// Clears the circuit breaker (e.g. after a fresh login provides new credentials).
export const resetRefreshState = () => {
  refreshFailed = false;
};

// Trips the breaker and runs the one-shot logout handler exactly once. Shared by the reactive
// refresh-failure path and the proactive session-expiry watcher so logout never double-fires.
const handlePermanentRefreshFailure = () => {
  if (refreshFailed) return;
  refreshFailed = true;
  if (typeof onRefreshFailure === 'function') {
    onRefreshFailure();
  }
};

// Checks if a JWT token is expired by decoding its payload
export const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    let base64 = token.split('.')[1];
    if (!base64) return true;
    base64 = base64.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) {
      base64 += '='.repeat(4 - pad);
    }
    const payload = JSON.parse(atob(base64));
    if (!payload?.exp) return true;
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

// Fetches new tokens using raw fetch to avoid Apollo/Axios interceptor recursion
const fetchNewTokens = async () => {
  // eslint-disable-next-line no-undef
  const accessToken = localStorage?.getItem(TOKEN);
  // eslint-disable-next-line no-undef
  const refreshToken = localStorage?.getItem(REFRESH_TOKEN_KEY);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);
  let response;
  try {
    // eslint-disable-next-line no-undef
    response = await fetch(process.env.REACT_APP_SERVER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
      body: JSON?.stringify({
        query: `query refreshToken($data: RefreshTokenInput) {
          refreshToken(data: $data) { token refreshToken }
        }`,
        variables: { data: { accessToken, refreshToken } },
      }),
    });
  } finally {
    clearTimeout(timeoutId);
  }
  if (!response?.ok) {
    // Transport-level failure (network/5xx) - treat as transient, keep refresh retryable
    throw new Error('Token refresh request failed');
  }
  const result = await response?.json();
  const newToken = result?.data?.refreshToken?.token;
  const newRefreshToken = result?.data?.refreshToken?.refreshToken;
  if (!newToken || !newRefreshToken) {
    // Server responded but refused to issue new tokens => refresh token invalid/expired.
    // Flag as an auth failure so the circuit breaker trips (vs a transient network error).
    const authError = new Error('Token refresh failed');
    authError.isAuthFailure = true;
    throw authError;
  }
  // eslint-disable-next-line no-undef
  localStorage.setItem(TOKEN, newToken);
  // eslint-disable-next-line no-undef
  localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
  return newToken;
};

// Singleton refresh - ensures only one refresh call at a time across both GraphQL and REST
// All concurrent callers share the same promise
export const refreshTokens = () => {
  // Circuit breaker: refresh token already rejected - don't hit the endpoint again
  if (refreshFailed) {
    return Promise.reject(new Error('Token refresh already failed'));
  }
  if (isRefreshing) return pendingRefreshPromise;
  isRefreshing = true;
  pendingRefreshPromise = fetchNewTokens()
    .then((token) => {
      refreshFailed = false;
      return token;
    })
    .catch((error) => {
      // Only trip the breaker + logout on a definitive auth failure (refresh token
      // rejected). Transient network errors stay retryable and do not force logout.
      if (error?.isAuthFailure) {
        handlePermanentRefreshFailure();
      }
      throw error;
    })
    .finally(() => {
      isRefreshing = false;
      pendingRefreshPromise = null;
    });
  return pendingRefreshPromise;
};

// Gets a valid token - proactively refreshes if expired
export const getValidToken = async () => {
  // eslint-disable-next-line no-undef
  let token = localStorage?.getItem(TOKEN);
  if (token && isTokenExpired(token)) {
    try {
      token = await refreshTokens();
    } catch {
      // Refresh failed, return existing token
    }
  }
  return token;
};

// Proactive idle logout: even with zero user activity, once the refresh token itself has
// expired the session can no longer be extended, so end it promptly instead of waiting for the
// next request-driven refresh. A lightweight client-side check runs on an interval; when a
// session is present (access token stored) but the refresh token has expired, it fires the same
// one-shot logout used on a rejected refresh. Started once at app bootstrap (see apollo.js).
let sessionWatchInterval = null;

export const startSessionExpiryWatch = (intervalMs = 30000) => {
  if (sessionWatchInterval) return;
  sessionWatchInterval = setInterval(() => {
    // eslint-disable-next-line no-undef
    const token = localStorage?.getItem(TOKEN);
    if (!token) return; // not logged in — nothing to watch
    // eslint-disable-next-line no-undef
    const refreshToken = localStorage?.getItem(REFRESH_TOKEN_KEY);
    if (isTokenExpired(refreshToken)) {
      handlePermanentRefreshFailure();
    }
  }, intervalMs);
};

export const stopSessionExpiryWatch = () => {
  if (sessionWatchInterval) {
    clearInterval(sessionWatchInterval);
    sessionWatchInterval = null;
  }
};

// Returns whether a refresh is currently in progress
export const getIsRefreshing = () => isRefreshing;

// Returns the pending refresh promise (for waiting on in-progress refreshes)
export const getPendingRefreshPromise = () => pendingRefreshPromise;

// Determines if an auth error should trigger token refresh vs immediate logout
export const isRefreshableAuthError = (extensions, errorMessage) => {
  if (errorMessage === 'User is disabled!') return false;
  if (extensions?.exception?.code === 405) return false;
  return extensions?.code === 'UNAUTHENTICATED';
};
